import {
  PageableDto,
  PageOfAdmission,
  Admission,
} from "@reside/reside-api-admission";
import {useInfiniteQuery, useQueryClient} from "react-query";
import {useClearAndCancelCache} from "../../../hooks/useClearAndCancelCache";
import {createConfig} from "../../../routers/useApiClientConfiguration";

import {getAuthorizationHeader} from "../../../models/admin-session/selectors";
import {ViewMode} from "../constants";
import {
  REPLICA_CATCH_UP_MS,
  WORKFLOWS_QUERY_KEY,
  replaceCardInWorkflowsData,
  restoreCard,
  suppressCard,
} from "./useWorkflowsQuery";
import type {SearchResponse} from "../../../services/WorkflowsService";
import {admissionsApi} from "../../../services/AdmissionsService";
import {AUTH_ERROR_STATUS} from "../../../services/api";

export const ADMISSIONS_BY_STATUS_QUERY_KEY = "admissionsByStatus";

export type AdmissionsByStatusQueryData = {
  pages: PageOfAdmission[];
};

/**
 * Replaces one admission (by id) inside every page of a single cached
 * admissionsByStatus query result.
 *
 * Returns the original `data` reference when the admission isn't present in
 * any page, so unrelated queries don't re-render.
 */
export const replaceAdmissionInQueryData = (
  data: AdmissionsByStatusQueryData | undefined,
  admission: Admission,
): AdmissionsByStatusQueryData | undefined => {
  if (!data?.pages?.length) {
    return data;
  }

  let changed = false;
  const pages = data.pages.map(page => {
    const hasAdmission = page?.content?.some(
      apiAdmission => apiAdmission.id === admission.id,
    );
    if (!hasAdmission) {
      return page;
    }

    changed = true;
    return {
      ...page,
      content: page.content.map(apiAdmission =>
        apiAdmission.id === admission.id ? admission : apiAdmission,
      ),
    };
  });

  return changed ? {...data, pages} : data;
};

type Params = {
  facilityIds: ReadonlyArray<string> | null;
  status?: Admission.StatusEnum;
  sortBy?: string;
  sortDirection?: PageableDto.DirectionEnum;
  /** Newly created user without a facility is not allowed to see admissions. */
  isAllowedToSeeAdmissions?: boolean;
  starredOnly?: boolean;
  /** View mode: grid vs list source */
  viewMode?: ViewMode;
  workflowType?: string | null;
};

const defaultSortParams = {
  sortBy: "updated",
  sortDirection: PageableDto.DirectionEnum.DESC,
};

export const useAdmissionsByStatusQuery = ({
  facilityIds,
  status,
  sortBy = defaultSortParams.sortBy,
  sortDirection = defaultSortParams.sortDirection,
  pageSize = 10,
  isAllowedToSeeAdmissions,
  starredOnly,
  viewMode,
  workflowType,
}: Params & {
  pageSize?: number;
}) => {
  const {clearAndCancelCache} = useClearAndCancelCache();
  const config = createConfig(clearAndCancelCache);

  // Don't run query if facilities haven't been loaded yet (null)
  // If isAllowedToSeeAdmissions is provided, also check that permission
  const facilitiesLoaded = facilityIds !== null;
  const shouldEnableQuery =
    facilitiesLoaded &&
    (isAllowedToSeeAdmissions === undefined || isAllowedToSeeAdmissions);

  return useInfiniteQuery(
    [
      ADMISSIONS_BY_STATUS_QUERY_KEY,
      {
        facilityIds,
        status,
        sortBy,
        sortDirection,
        starredOnly,
        viewMode,
        workflowType,
      },
    ],
    async ({pageParam = 0}) => {
      const {data} = await admissionsApi.searchAdmissionUsingPOST(
        {
          facilityIds,
          statuses: status ? [status as any] : undefined,
          pageable: {
            page: pageParam,
            size: pageSize,
            properties: [sortBy],
            direction: sortDirection,
          },
          starredOnly,
          workflowType: workflowType ?? undefined,
        } as any,
        getAuthorizationHeader(),
        {query: viewMode ? {viewMode} : undefined},
      );
      return data;
    },
    {
      getNextPageParam: ({content, totalPages}, allPages) => {
        const page = allPages.length;
        const isLastPage = totalPages === page;

        return content.length === pageSize && !isLastPage ? page : false;
      },
      cacheTime: 0,
      refetchOnMount: false,
      onError: async (err: Response) => {
        // Temporary untill all admissions endpoints are rewriten in python
        if (
          err.status === AUTH_ERROR_STATUS.FORBIDDEN ||
          err.status === AUTH_ERROR_STATUS.UNAUTHORIZED
        ) {
          config.logout();
        }
      },
      enabled: shouldEnableQuery,
      onSuccess: data => {
        const lastPage = data.pages[data.pages.length - 1];
        if (
          data.pages.length > 0 &&
          lastPage?.totalStarredAdmissions !== undefined
        ) {
          sessionStorage.setItem(
            "totalStarredAdmissions",
            lastPage.totalStarredAdmissions.toString(),
          );
          // Dispatch event to notify other components
          window.dispatchEvent(new Event("starredAdmissionsUpdated"));
        }
      },
    },
  );
};

/*
 * The reconciling fetches below wait `REPLICA_CATCH_UP_MS`, the same allowance the pre-flight
 * overlay uses: one figure for how long the replica takes, so the two paths cannot disagree.
 *
 * A single reconciling fetch, not a poll: the card is already off the board by then, so this is
 * only what puts it back in its new column. Past the worst case, not the typical one: etl-extract
 * picks a write up at once, but etl-transform polls every 5s, so a write can wait that long before
 * ds_workflow has it. At 6s the margin was a second, and a reconciling fetch that read the old row
 * put the card back where it was.
 */

export const useAdmissionsByStatusQueryUtils = () => {
  const cache = useQueryClient();

  /**
   * Refresh the grid after a write that leaves the row in place -- a star, or a workflow type
   * set on a pending admission. The write lands in `reside` and search reads the replica, so a
   * refetch straight away returns the old row and overwrites what the user just did. Waits out
   * replication first, as the reconciling fetch below does.
   */
  const refetchAfterReplication = () => {
    setTimeout(() => {
      cache.refetchQueries([WORKFLOWS_QUERY_KEY]);
    }, REPLICA_CATCH_UP_MS);
  };

  return {
    refetchAfterReplication,
    /**
     * Refresh the board after a write.
     *
     * `mutatedAdmissionId` is what makes the result truthful. Approve, archive and delete go
     * through Java while the board reads the replica, so the refetch below lands inside the
     * replication window and hands back the row exactly as it was -- the card returns to the
     * column the user just moved it out of, and with refetchOnMount off nothing corrects it
     * until they search, change a filter or reload. Dropping the card before that refetch, and
     * asking again once replication has had its moment, is what keeps the board agreeing with
     * the toast.
     */
    refetchAllAdmissionsByStatusQueries: async (
      mutatedAdmissionId?: string,
    ) => {
      /*
       * Held back rather than deleted from the cache. Any fetch inside the lag window returns
       * the pre-mutation row, not just the one below, so editing the cache would leave the next
       * refetch -- a second approve, or the one starring fires -- free to write the card back.
       * Held back first, so that refetch never paints the card in its old column.
       *
       * Lifted when the reconciling fetch has answered, not on a second timer: that is the
       * event it is waiting for. If the replica is somehow still behind, the card reappears
       * where it was, which is the honest thing to show.
       */
      if (mutatedAdmissionId) {
        suppressCard(mutatedAdmissionId);
      }

      await Promise.all([
        cache.refetchQueries([ADMISSIONS_BY_STATUS_QUERY_KEY]),
        // The grid caches under its own key, so invalidating only the Java one leaves a card
        // sitting in its old column until a full reload.
        cache.refetchQueries([WORKFLOWS_QUERY_KEY]),
      ]);

      if (!mutatedAdmissionId) {
        return;
      }

      setTimeout(async () => {
        try {
          await cache.refetchQueries([WORKFLOWS_QUERY_KEY]);
        } finally {
          restoreCard(mutatedAdmissionId);
        }
      }, REPLICA_CATCH_UP_MS);
    },
    /** Refresh the board after a star or unstar; see refetchAfterReplication. */
    refetchAfterStarToggle: refetchAfterReplication,
    refetchAdmissionsByStatusQuery: (params: Params) => {
      // Partial key: the grid's query key carries filters this caller does not know.
      cache.refetchQueries([WORKFLOWS_QUERY_KEY]);

      if (params.viewMode) {
        return cache.refetchQueries([
          ADMISSIONS_BY_STATUS_QUERY_KEY,
          {...defaultSortParams, ...params},
        ]);
      }
      cache.refetchQueries([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...defaultSortParams, ...params, viewMode: "grid" as const},
      ]);
      return cache.refetchQueries([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...defaultSortParams, ...params, viewMode: "list" as const},
      ]);
    },
    /**
     * Updates a single admission in every cached admissionsByStatus query,
     * regardless of that query's params (status, sortBy/sortDirection,
     * starredOnly, viewMode, workflowType, ...).
     *
     * The dashboard grid/list columns each run their own
     * `useAdmissionsByStatusQuery` with a different combination of those
     * params, so we can't know (or reconstruct) which exact query key(s)
     * currently hold the admission being patched. Passing a partial key here
     * (just `ADMISSIONS_BY_STATUS_QUERY_KEY`) makes react-query match *every*
     * query whose key starts with it, so the update reaches whichever
     * grid/list/sort/workflow query the admission happens to be cached in.
     */
    updateAdmissionInCache: (admission: Admission) => {
      cache.setQueriesData<AdmissionsByStatusQueryData>(
        [ADMISSIONS_BY_STATUS_QUERY_KEY],
        currentData => replaceAdmissionInQueryData(currentData, admission),
      );
      /*
       * And the cache the dashboard actually reads. Both views moved to the workflow service,
       * so patching only the key above left a ticked checklist item and a finished PCC upload
       * invisible until a reload — and a refetch is not the alternative, because the replica is
       * a second or two behind the Java write and would return the row as it was.
       */
      cache.setQueriesData<{pages: SearchResponse[]}>(
        [WORKFLOWS_QUERY_KEY],
        currentData =>
          replaceCardInWorkflowsData(
            currentData,
            admission as unknown as {id: string} & Record<string, unknown>,
          ) as {pages: SearchResponse[]},
      );
    },
  };
};
